/**
 * Per-row routing for the unified chat list.
 *
 * The backend ``/v2/chat/chats`` endpoint returns both legacy Evidence
 * chats (``version === 1``) and harness-backed agent chats
 * (``version === 2``) in the same list. Each row dispatches to a
 * different detail surface:
 *
 *   - v1 -> ``/scribe/chat/[id]`` — embedded-messages render path, the
 *     in-process Evidence LLM loop owns the conversation.
 *   - v2 -> ``/scribe/chat/projects/[id]`` — harness-backed render
 *     path, the Projects orchestrator + Evidence sub-agent drive the
 *     conversation (and optional desktop / schedule tools).
 *
 * This module centralises that dispatch so every list surface (main
 * sidebar, mobile drawer, embed page, floating ask-Heidi) routes
 * identically. New surfaces should call ``getChatHref`` rather than
 * building the path inline.
 */

/**
 * Minimal row shape the dispatch needs. Both the auto-generated
 * ``ChatListItem`` and the local ``Chat`` domain mirror satisfy this,
 * so callers don't need to coerce types.
 */
export interface ChatRoutingFields {
  id: string;
  /**
   * Storage discriminator from the backend list projection. ``1`` =
   * legacy Evidence chat; ``2`` = harness-backed agent chat. The
   * default of ``1`` lets the function work against older API
   * responses where the field is missing (they're always v1).
   */
  version?: 1 | 2;
}

export const getChatListVersion = (chat: ChatRoutingFields): 1 | 2 =>
  chat.version === 2 ? 2 : 1;

export const isWorkHarnessChat = (chat: ChatRoutingFields): boolean =>
  getChatListVersion(chat) === 2;

/**
 * When Work/Projects is off, harness-backed rows aren't openable on a
 * Work detail route — hide them so the sidebar doesn't offer clicks that
 * land on ``EvidenceHomeFallback`` under a ``/projects`` URL.
 */
export const filterChatsForWorkAvailability = <T extends ChatRoutingFields>(
  chats: T[],
  isWorkEnabled: boolean
): T[] => {
  if (isWorkEnabled) return chats;
  return chats.filter((chat) => !isWorkHarnessChat(chat));
};

/**
 * Resolve the detail-page href for a chat list row.
 *
 * Keep this synchronous and dependency-free — it's called inline in
 * click handlers across many surfaces and should not reach for hooks
 * or router state. Pure function in, pure string out.
 */
export const getChatHref = (chat: ChatRoutingFields): string => {
  if (isWorkHarnessChat(chat)) return `/scribe/chat/projects/${chat.id}`;
  return `/scribe/chat/${chat.id}`;
};
